import { useState } from 'react';
import { useAuth } from '@/lib/auth/AuthContext';
import { post } from 'aws-amplify/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';

type Environment = 'prod' | 'uat' | 'dev'

export default function EventOpsPage() {
  const { session } = useAuth();
  const { toast } = useToast();
  const [eventId, setEventId] = useState('');
  const [environment, setEnvironment] = useState<Environment>('prod');
  const [result, setResult] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const makeApiCall = async (action: 'redeploy' | 'tasks') => {
    if (action === 'redeploy' && !eventId.trim()) {
      toast({
        title: "Error",
        description: "Please enter an event ID for redeployment",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);
      setResult(`${action === 'redeploy' ? 'Redeploying container' : 'Fetching tasks'}...`);
      
      let endpoint = '';
      let body = {};

      if (action === 'redeploy') {
        endpoint = '/admin/redeployContainer';
        body = { eventId, environment };
      } else {
        endpoint = '/admin/eventContainerTasks';
        body = { environment };
      }
      
      const response = post({
        apiName: 'dashboard',
        path: endpoint,
        options: {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.tokens?.idToken}`
          },
          withCredentials: true,
          body
        }
      });

      const { body: responseBody } = await response.response;
      const data = await responseBody.json();
      setResult(JSON.stringify(data, null, 2));
      
      toast({
        title: "Success",
        description: `${action === 'redeploy' ? 'Container redeployment' : 'Task fetch'} completed successfully`,
      });
    } catch (error) {
      console.error(`Error ${action}:`, error);
      const errorMessage = `Failed to ${action}: ${error instanceof Error ? error.message : 'Unknown error'}`;
      setResult(errorMessage);
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const redeployContainer = () => makeApiCall('redeploy');
  const getEventContainerTasks = () => makeApiCall('tasks');

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Event Operations</h1>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Container Management</CardTitle>
          <CardDescription>
            Manage event containers and view running tasks
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="eventId">Event ID (for redeployment)</Label>
              <Input
                id="eventId"
                type="text"
                placeholder="Enter event ID"
                value={eventId}
                onChange={(e) => setEventId(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="environment">Environment</Label>
              <Select value={environment} onValueChange={(value) => setEnvironment(value as Environment)}>
                <SelectTrigger className="text-white">
                  <SelectValue placeholder="Select environment" />
                </SelectTrigger>
                <SelectContent className="text-white">
                  <SelectItem value="prod" className="text-white">prod</SelectItem>
                  <SelectItem value="uat" className="text-white">uat</SelectItem>
                  <SelectItem value="dev" className="text-white">dev</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button 
              onClick={redeployContainer} 
              disabled={isLoading}
              variant="default"
              className="text-white"
            >
              {isLoading ? "Processing..." : "Redeploy Container"}
            </Button>
            
            <Button 
              onClick={getEventContainerTasks} 
              disabled={isLoading}
              variant="outline"
              className="text-white"
            >
              {isLoading ? "Processing..." : "Get Tasks"}
            </Button>
          </div>
          
          {result && (
            <div className="space-y-2">
              <Label htmlFor="result">Result</Label>
              <textarea
                id="result"
                className="w-full h-40 p-2 text-sm font-mono border rounded-md bg-muted"
                value={result}
                readOnly
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
